import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ENABLED_LOCALES,
  LOCALE_LABELS,
  LOCALE_SHORT_LABELS,
  localizedPath,
} from '@/shared/config/locales';
import { cn, type AppLocale } from '@/shared/lib';

/**
 * Links to the current page in each other language.
 *
 * Plain anchors on purpose: switching language is a full navigation into the
 * other locale's URL space, so the new page loads that language alone and
 * crawlers can follow the alternates like any other link.
 *
 * With a single enabled locale there is nothing to switch to, so the control
 * is absent rather than a lone, inert label.
 */
export const LanguageSwitcher = ({
  locales = ENABLED_LOCALES,
}: {
  locales?: readonly AppLocale[];
}) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const current = i18n.language as AppLocale;

  if (locales.length < 2) return null;

  return (
    <nav aria-label={t('footer.language')} className="flex items-center gap-1 text-sm">
      {locales.map((locale) =>
        locale === current ? (
          <span
            key={locale}
            aria-current="true"
            className="rounded-lg bg-gray-800 px-2.5 py-1 font-semibold text-white"
          >
            {LOCALE_SHORT_LABELS[locale]}
          </span>
        ) : (
          <a
            key={locale}
            href={localizedPath(locale, pathname)}
            hrefLang={locale}
            lang={locale}
            title={LOCALE_LABELS[locale]}
            className={cn(
              'rounded-lg px-2.5 py-1 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white',
            )}
          >
            {LOCALE_SHORT_LABELS[locale]}
          </a>
        ),
      )}
    </nav>
  );
};
